import React, { useState, useRef, useEffect } from 'react';
import { Menu, Search, Sun, Moon, X, ArrowLeft, LogOut, User as UserIcon, Plus, Settings as SettingsIcon, LogIn, UserPlus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { searchFocusRef } from '../lib/searchFocus';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';

const Header = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, openAuthModal, logout } = useAuth();
  const [query, setQuery] = useState('');
  const [mobileSearch, setMobileSearch] = useState(false);

  // Sync the search input to the URL `?q=` param whenever the route
  // changes. This means the Home > Search tab's "Clear All" button
  // (which strips `?q=`) also empties the header input, and any deep
  // link that arrives with `?q=foo` populates the box on load.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setQuery(params.get('q') || '');
  }, [location.search]);

  // Reset the search bar whenever the user navigates AWAY from the
  // home page — e.g. clicks a sidebar entry while on the Search tab.
  // Home's own tab-switch calls `collapseNow()` (mobile-only) directly
  // (see Home.jsx handleTabClick) — that path preserves the query so
  // intra-Home tab switching restores state on return. This effect
  // handles the harder case: leaving Home entirely. `resetNow()` is
  // the full wipe: collapse + clear input + strip `?q=`.
  useEffect(() => {
    if (location.pathname !== '/' && typeof searchFocusRef.resetNow === 'function') {
      searchFocusRef.resetNow();
    }
  }, [location.pathname]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // Ref on the actual `<input>` so external callers (Home tab) can
  // programmatically clear + focus it via the `search:focus` window
  // event below. Kept as a ref instead of an id lookup so it survives
  // remounts and doesn't depend on unique-in-DOM assumptions.
  const searchInputRef = useRef(null);

  // Populate the singleton so external components (Home tab chips)
  // can drive the search bar SYNCHRONOUSLY inside their click
  // handlers. Registered on mount, cleared on unmount so re-mounts
  // don't leak stale refs.
  //
  // `focusNow()` handles the "expand + clear + focus" trigger fired
  // when the user taps the Search chip, Clear All, or dismisses the
  // last recent-search pill. Everything is imperative so the mobile
  // on-screen keyboard reliably pops up (see searchFocus.js for the
  // gesture-chain explanation).
  //
  // `collapseNow()` handles the reverse — collapsing the mobile
  // expanded search bar when the user activates a non-Search tab.
  useEffect(() => {
    searchFocusRef.focusNow = () => {
      // Focus the search input. On desktop (Tailwind `md` breakpoint
      // and up — 768px), the input is always visible in the header
      // so we simply focus it without touching layout. On mobile,
      // the input is normally hidden (`hidden md:flex`), so we must
      // expand the mobile search bar (`mobileSearch=true`) to make
      // it reachable AND to give it enough room — the menu/logo/
      // right-icons collapse out of the way. This preserves the
      // "no rearrangement on desktop" contract while keeping mobile
      // usable.
      //
      // We do NOT clear the input value here — Header's URL-sync
      // effect keeps the input in step with `?q=`, so intra-Home
      // tab switches that restore a previous query will already
      // have populated the input by the time this runs.
      const el = searchInputRef.current;
      if (!el) return;
      const isMobile = typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(max-width: 767px)').matches
        : false;
      if (isMobile) {
        setMobileSearch(true);
        let node = el.parentElement;
        while (node && node !== document.body) {
          if (node.classList && node.classList.contains('hidden')) {
            node.classList.remove('hidden');
            node.classList.add('flex');
          }
          node = node.parentElement;
        }
      }
      try {
        el.focus({ preventScroll: false });
      } catch (err) {
        console.warn('search focusNow failed', err);
      }
    };
    searchFocusRef.collapseNow = () => {
      // Pure mobile-bar collapse — flips `mobileSearch` back to
      // false so the icon-only chrome returns. Does NOT touch the
      // query text or URL, so intra-Home tab switches (Search →
      // Nearby → Search) preserve the previous search state.
      // Desktop is unaffected (search bar always visible).
      setMobileSearch(false);
      const el = searchInputRef.current;
      if (el && document.activeElement === el) {
        try { el.blur(); } catch { /* noop */ }
      }
    };

    searchFocusRef.resetNow = () => {
      // Full reset: collapse the mobile bar AND wipe the query
      // text + strip any `?q=` from the URL. Used when the user
      // navigates AWAY from Home entirely (sidebar item, external
      // link, etc.) so returning to Home starts fresh.
      setMobileSearch(false);
      const el = searchInputRef.current;
      if (el) {
        if (document.activeElement === el) {
          try { el.blur(); } catch { /* noop */ }
        }
        el.value = '';
      }
      setQuery('');
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.has('q')) {
          url.searchParams.delete('q');
          window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        }
      } catch { /* noop */ }
    };
    return () => {
      searchFocusRef.focusNow = null;
      searchFocusRef.collapseNow = null;
      searchFocusRef.resetNow = null;
    };
  }, []);

  // Notification badge lives on the Home "Notifications" tab now.
  // See `contexts/NotificationsContext.jsx` for the shared count logic
  // (poll + SSE + event listeners) that used to live here.

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      // Route to the Home page with the query — Home reads `?q=` and
      // renders matching prayers inside the "All" tab.
      navigate(`/?q=${encodeURIComponent(q)}`);
    }
  };

  const initial = (user?.name || user?.email || 'U').trim()[0]?.toUpperCase();

  return (
    <header className="sticky top-0 z-50 flex items-center justify-between gap-2 sm:gap-4 px-3 sm:px-4 h-14 bg-white dark:bg-[#0f0f0f] text-zinc-900 dark:text-white">
      <div className={`items-center gap-2 sm:gap-4 ${mobileSearch ? 'hidden' : 'flex'}`}>
        <button onClick={onMenuToggle} className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors" aria-label="Menu">
          <Menu size={22} />
        </button>
        <button onClick={() => navigate('/')} data-testid="site-logo" className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="flex items-center justify-center text-amber-600 dark:text-amber-300 shrink-0"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 3 C 9.5 5, 7.5 9, 7 14 C 6.7 17, 8 19, 9.5 20 L 11.5 20 Z" />
              <path d="M13 3 C 14.5 5, 16.5 9, 17 14 C 17.3 17, 16 19, 14.5 20 L 12.5 20 Z" />
              <line x1="12" y1="3.5" x2="12" y2="20" />
            </svg>
          </span>
          {/* Mobile abbreviation — the full "Pray Now Together"
              wordmark is too wide for phone widths, so we render "PN"
              in the same bold Roboto and the "T" in the Caveat cursive
              script to mirror the desktop wordmark's mixed-family look. */}
          <span className="flex sm:hidden items-baseline whitespace-nowrap">
            <span
              className="text-xl"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: '0.3em' }}
            >PN</span>
            <span
              className="text-xl text-amber-600 dark:text-amber-300"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: '0.3em', lineHeight: 1 }}
            >T</span>
          </span>
          {/* Desktop wordmark — "Pray Now" in bold Roboto, "Together"
              in the Caveat cursive script that's already preloaded in
              index.html. Baseline-aligned so the mixed families sit
              cleanly on the same visual line. */}
          <span className="hidden sm:flex items-baseline whitespace-nowrap">
            <span
              className="text-2xl"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: '0.3em' }}
            >Pray Now</span>
            <span
              className="text-2xl ml-2 text-amber-600 dark:text-amber-300"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300, letterSpacing: '0.3em', lineHeight: 1 }}
            >Together</span>
          </span>
        </button>
      </div>

      <div className={`flex-1 max-w-2xl items-center gap-2 ${mobileSearch ? 'flex' : 'hidden md:flex'}`}>
        {mobileSearch && (
          <button onClick={() => setMobileSearch(false)} className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800">
            <ArrowLeft size={22} />
          </button>
        )}
        <form onSubmit={handleSearch} className="flex flex-1 items-center">
          <div className="flex flex-1 items-center h-10 border border-zinc-300 dark:border-zinc-700 rounded-l-full focus-within:border-blue-500 dark:focus-within:border-blue-500 bg-white dark:bg-[#121212] overflow-hidden">
            <input ref={searchInputRef} value={query} onChange={(e) => setQuery(e.target.value)} type="text" placeholder="Search" className="flex-1 bg-transparent outline-none px-4 text-[16px] placeholder-zinc-500" />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="p-2 mr-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-full">
                <X size={18} />
              </button>
            )}
          </div>
          <button type="submit" className="h-10 w-14 sm:w-16 flex items-center justify-center border border-l-0 border-zinc-300 dark:border-zinc-700 rounded-r-full bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors">
            <Search size={20} />
          </button>
        </form>
      </div>

      <div className={`items-center gap-1 sm:gap-2 ${mobileSearch ? 'hidden' : 'flex'}`}>
        <button onClick={() => setMobileSearch(true)} className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 md:hidden">
          <Search size={22} />
        </button>
        <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-colors" title="Toggle theme">
          {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
        </button>
        {/* + icon is always visible; anonymous users get sent to sign-in. */}
        <button
          onClick={() => user ? navigate('/add-prayer') : openAuthModal()}
          data-testid="header-add-prayer-btn"
          className="p-2 rounded-full hover:bg-amber-100 dark:hover:bg-amber-950/40 text-amber-600 dark:text-amber-400 transition-colors"
          title="Share a prayer"
          aria-label="Share a new prayer"
        >
          <Plus size={24} strokeWidth={2.5} />
        </button>

        {user ? (
          <div ref={menuRef} className="relative">
            <button onClick={() => setMenuOpen(o => !o)} className="ml-1 w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-medium">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : initial}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-11 w-72 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-xl py-2 z-50">
                <div className="px-4 py-3 flex items-center gap-3 border-b border-zinc-200 dark:border-zinc-800">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
                    {user.picture ? <img src={user.picture} alt={user.name} className="w-full h-full object-cover"/> : initial}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{user.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{user.email}</div>
                  </div>
                </div>
                <button onClick={() => { setMenuOpen(false); navigate('/profile'); }} data-testid="header-profile-link" className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-3">
                  <UserIcon size={18} /> Profile
                </button>
                <button onClick={() => { setMenuOpen(false); navigate('/settings'); }} data-testid="header-settings-link" className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-3">
                  <SettingsIcon size={18} /> Settings
                </button>
                <button onClick={async () => { setMenuOpen(false); await logout(); navigate('/'); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 flex items-center gap-3">
                  <LogOut size={18} /> Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="header-signin-btn"
                aria-label="Sign in or create account"
                title="Sign in or create account"
                className="ml-1 inline-flex items-center justify-center w-9 h-9 rounded-full border border-zinc-300 dark:border-zinc-700 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
              >
                <UserIcon size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onSelect={() => openAuthModal('login')}
                data-testid="header-signin-menu-login"
                className="cursor-pointer"
              >
                <LogIn size={16} className="mr-2" />
                Sign-In
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => openAuthModal('register')}
                data-testid="header-signin-menu-register"
                className="cursor-pointer"
              >
                <UserPlus size={16} className="mr-2" />
                Create Account
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
};

export default Header;
